import React, { FC } from 'react'
import { useSelector } from 'react-redux'

import { useBotUsage } from 'hooks/useBotUsage'
import { useBotUsageContext } from 'contexts/BotUsageContext'
import { isQcsBot } from 'constants/qcs'
import { USAGE_METRIC } from 'constants/usageLimits'

import UsageMeter from './UsageMeter'
import CarePlanAuditUnavailableCard from './CarePlanAuditUnavailableCard'
import SupervisionUnavailableCard from './SupervisionUnavailableCard'
import * as S from './UsageMeter.style'

interface Props {
  botId: string | number
  /** Which allowances to show here — normally the one this page spends. */
  metrics: string[]
  canRequestIncrease?: boolean
}

/**
 * The metered allowances for one page, shown where the allowance is spent.
 *
 * Supervision sessions belong on Professionals and care plan audits on Service
 * Users, rather than both on a summary screen: someone about to run one should
 * not have to go and look up whether they can.
 *
 * Only QCS organisations are metered, so this is inert everywhere else.
 */
export const UsageStrip: FC<Props> = ({ botId, metrics, canRequestIncrease = true }) => {
  const activeBot = useSelector((state: any) => state.activeBot)
  const metered = isQcsBot(activeBot || botId)

  // Prefer the page's shared read. The own-read fallback keeps this usable on a
  // page that has not mounted a provider, but is disabled whenever one has, so
  // the allowance is never fetched twice for the same screen.
  const shared = useBotUsageContext()
  const own = useBotUsage(botId, metered && !shared)
  const usage = shared || own

  if (!metered || !usage.hasData) return null

  const cards = metrics
    .map(metric => ({
      metric,
      value: usage.metric(metric),
      featureEnabled: usage.isFeatureEnabled(metric),
    }))
    .filter(entry => entry.value || !entry.featureEnabled)

  if (cards.length === 0) return null

  return (
    <S.MeterGroup>
      {cards.map(({ metric, value, featureEnabled }) => {
        if (!featureEnabled && metric === USAGE_METRIC.CARE_PLAN_AUDITS) {
          return (
            <CarePlanAuditUnavailableCard key={metric} botId={botId} canRequestIncrease={canRequestIncrease} />
          )
        }
        if (!featureEnabled && metric === USAGE_METRIC.SUPERVISION_SESSIONS) {
          return <SupervisionUnavailableCard key={metric} botId={botId} canRequestIncrease={canRequestIncrease} />
        }
        return (
          <UsageMeter
            key={metric}
            metric={metric}
            usage={value}
            periodEnd={usage.periodEnd}
            botId={botId}
            canRequestIncrease={canRequestIncrease}
            featureEnabled={featureEnabled}
          />
        )
      })}
    </S.MeterGroup>
  )
}

export default UsageStrip
